import type { WorkspaceProjection } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { useWorkspaceIndexer } from '../../hooks/useWorkspaceIndexer';

interface WorkspaceIndexerStatusProps {
  projection: WorkspaceProjection;
  workspacePath: string;
}

function stateLabel(projection: WorkspaceProjection): string {
  return projection.index.progress === 'indexing' ? `Indexing ${projection.index.indexed}` : 'Idle';
}

function stateBadgeColor(projection: WorkspaceProjection): string {
  if (projection.index.health === 'degraded') {
    return 'bg-error-subtle text-error-text';
  }
  return projection.index.progress === 'indexing'
    ? 'bg-warning-subtle text-warning-text'
    : 'bg-muted text-foreground';
}

export function WorkspaceIndexerStatus({ projection, workspacePath }: WorkspaceIndexerStatusProps) {
  const canIndexLocally =
    projection.assignment.routing_class === 'local' && workspacePath.length > 0;
  const { isStarting, error, reindex } = useWorkspaceIndexer(
    canIndexLocally
      ? {
          workspaceId: projection.workspace_id,
          workspacePath,
          assignmentEpoch: projection.assignment.epoch,
        }
      : null
  );
  const isBusy = isStarting || projection.index.progress === 'indexing';

  return (
    <div className="space-y-1" data-testid="workspace-indexer-status">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-caption font-medium text-muted-foreground">Search Index</span>
          <span
            className={`rounded px-1.5 py-0.5 text-caption font-medium ${stateBadgeColor(projection)}`}
          >
            {stateLabel(projection)}
          </span>
        </div>
        {canIndexLocally && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-caption"
            disabled={isBusy}
            onClick={(event) => {
              event.stopPropagation();
              void reindex();
            }}
            data-testid="reindex-button"
          >
            Re-index
          </Button>
        )}
      </div>

      <p
        className={
          projection.index.health === 'degraded'
            ? 'text-caption text-destructive'
            : 'text-caption text-muted-foreground'
        }
        data-testid="workspace-index-health"
        data-health={projection.index.health}
        data-progress={projection.index.progress}
      >
        {projection.index.health === 'degraded'
          ? 'Search is behind; a full re-index is required'
          : projection.index.progress === 'indexing'
            ? 'Search is catching up'
            : 'Search is current'}
      </p>

      {projection.index.indexed > 0 && (
        <p className="text-caption text-muted-foreground">{projection.index.indexed} files indexed</p>
      )}

      {error && (
        <p className="text-caption text-destructive" data-testid="workspace-index-start-error">
          {error.message}
        </p>
      )}
    </div>
  );
}
